import React from 'react';
import moment from 'moment';
import AccountIcon from "../../components/accounts/AccountIcon.jsx";
import { logoForAccount } from "../../../utilities/accountIcons.js";
import DragHandle from "../../elements/DragHandle.jsx";
import MoneyComponent from "../../elements/MoneyComponent.jsx";
import { moneyFormatFromCents } from "../../../utilities/moneyFormat.js";
import { isClosedAccount } from "../../../utilities/closedAccounts.js";
import { countsInTotals } from "../../../utilities/accountSections.js";

/** A balance older than this says how old it is; a recent one needs no comment. */
const STALE_AFTER_DAYS = 31;

/**
 * Why a row reads the way it does, in a few words under its name: where it is held, its last digits
 * (or, with none, the `hint` telling it from a same-named account -- see twinHints()), and anything
 * that makes its number less than it looks -- closed, left out of the totals, old, or missing.
 */
export function accountRowNotes(account, institution, balance, { showInstitution = true, now = moment(), hint = null } = {}) {
	const notes = [];
	if(showInstitution && institution) {
		notes.push({ key: 'institution', text: institution.getName() });
	}
	if(account.mask) {
		notes.push({ key: 'mask', text: `••${account.mask}`, title: 'Last digits of the account number' });
	} else if(hint) {
		notes.push({ key: 'hint', text: hint, title: 'Another account has this name and neither has its last digits' });
	}
	if(isClosedAccount(account)) {
		notes.push({ key: 'closed', text: 'Closed' });
	}
	if(!account.shouldAddToTotalAssets()) {
		notes.push({ key: 'not_counted', text: 'Not in total' });
	}
	const lastUpdated = account.getLastUpdated();
	if(balance === null || !lastUpdated) {
		notes.push({ key: 'no_balance', text: 'No balance yet' });
	} else if(now.diff(lastUpdated, 'days') > STALE_AFTER_DAYS) {
		notes.push({ key: 'stale', text: `Updated ${lastUpdated.from(now)}`, title: lastUpdated.format('LL') });
	}
	return notes;
}

/**
 * One account on one line. The whole row opens it -- its summary on the page, its editor while
 * arranging, which `opens` ('summary' or 'editor') says so the row is named for what it does -- so
 * the row itself carries no controls, only a drag handle while arranging.
 */
export default function AccountRow({ account, institution, balance, limit = null, showInstitution = true, hint = null, dragHandleProps = null, opens = 'summary', onOpen }) {
	const notes = accountRowNotes(account, institution, balance, { showInstitution, hint });
	const classes = ['account_row'];
	if(!countsInTotals(account)) {
		classes.push('not_counted');
	}

	function open(event) {
		onOpen(account, institution, event.currentTarget);
	}

	return (
		<div
			className={classes.join(' ')}
			role='button'
			tabIndex={0}
			data-account-id={account.id}
			aria-label={opens === 'editor' ? `Edit ${account.getDisplayName()}` : `Open ${account.getDisplayName()}`}
			onClick={open}
			onKeyDown={(event) => {
				if(event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					open(event);
				}
			}}
		>
			{dragHandleProps &&
			<DragHandle
				{...dragHandleProps}
				onClick={(event) => event.stopPropagation()}
			/>
			}
			<AccountIcon
				icon={account.getIcon()}
				logo={logoForAccount(account, institution)}
				color={account.color || institution.getColor()}
				size={32}
			/>
			<span className='account_row_text'>
				<span className='account_row_name'>{account.getDisplayName()}</span>
				{notes.length > 0 &&
				<span className='account_row_notes'>
					{notes.map((note) => (
						<span key={note.key} className={`account_row_note ${note.key}`} title={note.title}>{note.text}</span>
					))}
				</span>
				}
			</span>
			<span className='account_row_amount'>
				{balance === null
					? <span className='account_row_no_balance'>&mdash;</span>
					: <MoneyComponent amount={balance} showPositive={false} showNegative={false} />}
				{limit &&
				<span className='account_row_limit'>of {moneyFormatFromCents(limit)}</span>
				}
			</span>
		</div>
	);
}
